import { cloneElement, useCallback, useEffect, useMemo, useRef } from "react";

import {
  Button,
  ButtonProps,
  Box,
  Flex,
  FlexProps,
  PlacementWithLogical,
  Popover,
  PopoverContent,
  PopoverContentProps,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";

import { DropDownList, DropDownOption, DropDownSection } from "ui/DropDown/DropDownList";
import { CaretDownIcon } from "ui/Icons/Icons";
import { Fade } from "ui/Transitions/Transitions";
import { BodyM } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

type DropDownState = "default" | "active" | "selected";

export type DropDownProps = {
  options?: DropDownOption[];
  sections?: DropDownSection[];
  value: string | null;
  onInput: (v: string | null) => void;
  children?: React.ReactNode;
  filterMode?: boolean;
  showScrollbar?: boolean;
  popoverStyles?: PopoverContentProps;
  listStyles?: Omit<FlexProps, "onInput">;
  listItemStyles?: Omit<FlexProps, "onInput">;
  scrollContainerRef?: HTMLElement | null;
  testId?: string;
  titleColor?: string;
  placement?: PlacementWithLogical;
} & Omit<ButtonProps, "value" | "onInput">;

const defaultStyle: ButtonProps = {
  backgroundColor: "white",
  color: "grey.800",
};

const activeStyle: ButtonProps = {
  backgroundColor: "pinkFlink.100",
  color: "pinkFlink.500",
};

const selectedStyle: ButtonProps = {
  backgroundColor: "pinkFlink.200",
  color: "pinkFlink.500",
};

const hoverStyle: ButtonProps = {
  backgroundColor: "pinkFlink.200",
  color: "pinkFlink.500",
};

const buttonStyleMap: Record<DropDownState, ButtonProps> = {
  default: defaultStyle,
  active: activeStyle,
  selected: selectedStyle,
};

export function DropDown({
  options,
  sections,
  value,
  onInput,
  children,
  filterMode = true,
  showScrollbar = false,
  popoverStyles,
  listStyles,
  listItemStyles,
  scrollContainerRef,
  testId,
  titleColor,
  placement,
  ...rest
}: DropDownProps) {
  const { isOpen, onToggle, onClose } = useDisclosure();

  const dropDownState: DropDownState = useMemo(() => {
    if (isOpen) {
      return "selected";
    }
    if (filterMode && value !== null) {
      return "active";
    }
    return "default";
  }, [isOpen, value, filterMode]);

  const handleInput = useCallback(
    (v: string | null) => {
      onInput(v);
      setTimeout(() => {
        onClose();
      }, 80);
    },
    [onInput, onClose],
  );

  const popoverRef = useRef<HTMLUListElement | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const scrollToTrigger = useCallback(() => {
    scrollContainerRef?.scrollTo({ top: triggerRef.current?.offsetTop, behavior: "smooth" });
  }, [scrollContainerRef]);
  const toggleDropDown = useCallback(() => {
    if (isNotNullNorUndefined(scrollContainerRef)) {
      scrollToTrigger();
      // Not sure why the next line works,
      // but it makes the dropdown open only after the scroll is finished
      setTimeout(onToggle, 0);
    } else {
      onToggle();
    }
  }, [onToggle, scrollContainerRef, scrollToTrigger]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const ref = popoverRef.current;
    if (!ref) {
      return;
    }
    const handleScroll = (e: WheelEvent | TouchEvent) => {
      // If element is not scrollable
      if (ref.scrollHeight <= ref.clientHeight) {
        // We cancel the scroll event so that it doesn't scroll the document's body
        e.preventDefault();
      }
    };
    if (isOpen) {
      ref.addEventListener("wheel", handleScroll);
      ref.addEventListener("touchmove", handleScroll);
      // eslint-disable-next-line consistent-return
      return () => {
        ref.removeEventListener("wheel", handleScroll);
        ref.removeEventListener("touchmove", handleScroll);
      };
    }
  }, [isOpen]);

  const allOptions = useMemo(
    () => options ?? sections?.flatMap((section) => section.options) ?? [],
    [options, sections],
  );
  const selectedOption = useMemo(
    () => allOptions.find(({ key }) => key === value),
    [allOptions, value],
  );
  const hasOptions = allOptions.length > 0;
  const buttonStyle = buttonStyleMap[dropDownState];

  return (
    <Popover placement={placement ?? "auto"} gutter={10} onClose={onClose} isOpen={isOpen}>
      <Fade in={isOpen} style={{ zIndex: "9", position: "fixed" }}>
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100%"
          height={isOpen ? "100%" : "0"}
          bgColor="rgba(0,0,0,0.2)"
          zIndex="9"
          data-testid="popover-overlay"
        />
      </Fade>
      <PopoverTrigger>
        <Button
          {...buttonStyle}
          _hover={hoverStyle}
          onClick={hasOptions ? toggleDropDown : () => {}}
          ref={triggerRef}
          data-testid={testId}
          rightIcon={<CaretDownIcon boxSize="24px" />}
          {...rest}
        >
          <Flex flex="1" align="center" overflow="hidden" whiteSpace="nowrap" gap="s100">
            {!filterMode && value && selectedOption ? (
              <>
                {selectedOption?.icon && cloneElement(selectedOption.icon, { boxSize: "24px" })}
                <BodyM
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  color={titleColor}
                >
                  {selectedOption.label}
                </BodyM>
              </>
            ) : (
              children
            )}
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent width="fit-content" mr="s100" border="none" {...popoverStyles}>
        <DropDownList
          options={options}
          sections={sections}
          onInput={handleInput}
          value={value}
          ref={popoverRef}
          testId={testId}
          listItemStyles={listItemStyles}
          showScrollbar={showScrollbar}
          {...listStyles}
        />
      </PopoverContent>
    </Popover>
  );
}
